import { LetterEventName, logLetterEvent } from "./helpers/logLetterEvent";
import { LetterStep, letterStore } from "src/js/stores/letter/store";
import {
  LoadRecipientsProps,
  useLoadRecipients,
} from "react-migration/domains/sites/hooks/useLoadRecipients/useLoadRecipients";
import { SELECT_RECIPIENTS_FORM_ID, SelectRecipientsForm } from "../forms/SelectRecipientsForm/SelectRecipientsForm";
import { loadRecipients, updateCurrentStep, updateLetterName } from "src/js/stores/letter/actions";

import { Button } from "react-migration/components/DeprecatedButton";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { Loading } from "react-migration/components/Loading";
import { SelectRecipientsFormData } from "../forms/SelectRecipientsForm/SelectRecipientsFormSchema";
import { SelectRecipientsFormProvider } from "../forms/SelectRecipientsForm/SelectRecipientsFormProvider";
import classNames from "clsx";
import { mergeFormDataWithRecipientData } from "../forms/SelectRecipientsForm/mergeFormDataWithRecipientData";
import { useFilters } from "../pipeline/PipelineProvider/hooks/useFilters";
import { useFormContext } from "react-hook-form";
import { useMemo } from "react";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

type SelectEditableRecipientsStepContentProps = {
  onSubmit: (formData: SelectRecipientsFormData) => void;
};

export const SelectEditableRecipientsStepContent = ({ onSubmit }: SelectEditableRecipientsStepContentProps) => {
  const { t } = useTranslation();

  const { selectedRecipientIds } = useSnapshot(letterStore);

  const { formState } = useFormContext<SelectRecipientsFormData>();

  const isNextEnabled = selectedRecipientIds && selectedRecipientIds?.length > 0 && formState.isValid;

  return (
    <div data-testid="select-editable-recipients-step" className="flex flex-col w-full h-full">
      <Header />
      <div
        data-testid="select-recipients-form-container"
        className={classNames("flex", "grow", "w-full", "overflow-hidden")}
      >
        <SelectRecipientsForm onSubmit={onSubmit} />
      </div>
      <Footer>
        <div className="flex justify-end grow">
          <Button.Primary disabled={!isNextEnabled} type="submit" form={SELECT_RECIPIENTS_FORM_ID}>
            {t("sites.letters.next_create_letter_button")}
          </Button.Primary>
        </div>
      </Footer>
    </div>
  );
};

export const SelectEditableRecipientsStep = () => {
  const { t } = useTranslation();
  const { selectedStageIds, letterName, selectedSiteIds } = useSnapshot(letterStore);
  const filters = useFilters();

  /** There are two main entry points to the letters flow at the moment
   * - A single site from the site card or a list of sites from the list view
   * - A selection from the Kanban board
   *
   * A single site or a list of sites must ignore all filters that are active and just do a site ids lookup
   * A selection must include any filtering params that are available in the kanban view
   *
   */
  let loadRecipientsProps: LoadRecipientsProps;

  if (selectedSiteIds?.length) {
    const emptyFilters = {
      searchText: "",
      localAuthorities: [],
      siteLabels: [],
      assignees: [],
      stageIds: [], //stageIds are ignored in useLoadRecipients
    };

    loadRecipientsProps = {
      siteIds: selectedSiteIds,
      filters: emptyFilters,
    };
  } else {
    loadRecipientsProps = {
      stageIds: selectedStageIds ?? undefined,
      filters: {
        ...filters,
        stageIds: [], //stageIds are ignored in useLoadRecipients so we remove here for confusion
      },
    };
  }

  const { loading, recipients } = useLoadRecipients(loadRecipientsProps);

  const recipientsFormData: SelectRecipientsFormData | undefined = useMemo(() => {
    return {
      recipients:
        recipients?.map(({ fullAddress: _f, ...recipient }) => ({
          ...recipient,
          isSelected: recipient.isGranted,
        })) ?? [],
    };
  }, [recipients]);

  const onFormSubmit = (formData: SelectRecipientsFormData) => {
    if (!recipients) return;

    if (!letterName) {
      updateLetterName(t("sites.letters.untitled_letter"));
    }

    const updatedRecipients = mergeFormDataWithRecipientData(recipients, formData);

    loadRecipients(updatedRecipients);
    updateCurrentStep(LetterStep.TemplateEditor);
    logLetterEvent({
      name: LetterEventName.LETTERS_FORM_SELECT_RECIPIENTS_STEP_CREATE_LETTER_BUTTON_CLICKED,
    });
  };

  return (
    <>
      {loading && (
        <div className={classNames("h-full", "w-full", "flex", "justify-center", "items-center", "overflow-hidden")}>
          <Loading />
        </div>
      )}
      {!loading && (
        <SelectRecipientsFormProvider data={recipientsFormData}>
          <SelectEditableRecipientsStepContent onSubmit={onFormSubmit} />
        </SelectRecipientsFormProvider>
      )}
    </>
  );
};
